/**
 * A new instance of deferred is constructed by calling `new DeferredPromse<T>()`.
 * The purpose of the deferred object is to expose the associated Promise
 * instance APIs that can be used for signaling the successful
 * or unsuccessful completion, as well as the state of the task.
 * @export
 * @class DeferredPromise
 * @implements {Promise<T>}
 * @template T
 * @example
 * const deferred = new DeferredPromse<string>();
 * console.log(deferred.state); // 'pending'
 *
 * deferred
 * .then(str => console.log(str))
 * .catch(err => console.error(err));
 *
 * deferred.resolve('Foo');
 * console.log(deferred.state); // 'fulfilled'
 * // deferred.reject('Bar');
 *
 * source: https://gist.github.com/GFoley83/5877f6c09fbcfd62569c51dc91444cf0
 *
 */
export class Deferred<T> implements Promise<T> {
  public [Symbol.toStringTag] = 'Promise';
  public id: number;
  private _promise: Promise<T>;
  private _reject!: (reason?: unknown) => void;
  private _resolve!: (value: T | PromiseLike<T>) => void;
  private _state: 'pending' | 'fulfilled' | 'rejected' = 'pending';

  constructor() {
    this._promise = new Promise<T>((resolve, reject) => {
      if (resolve) {
        this._resolve = resolve;
      }
      this._reject = reject;
    });
    this.id = Math.random() * (500 - 1) + 1;
  }

  public get state(): 'pending' | 'fulfilled' | 'rejected' {
    return this._state;
  }

  public finally(onfinally?: () => void): Promise<T> {
    return this._promise.finally(onfinally);
  }

  public then<TResult1, TResult2>(
    onfulfilled?: (value: T) => TResult1 | PromiseLike<TResult1>,
    onrejected?: (reason: unknown) => TResult2 | PromiseLike<TResult2>,
  ): Promise<TResult1 | TResult2> {
    return this._promise.then(onfulfilled, onrejected);
  }

  public catch<TResult>(onrejected?: (reason: unknown) => TResult | PromiseLike<TResult>): Promise<T | TResult> {
    return this._promise.catch(onrejected);
  }

  public resolve(value: T | PromiseLike<T>): void {
    this._resolve(value);
    this._state = 'fulfilled';
  }

  public reject(reason?: unknown): void {
    this._reject(reason);
    this._state = 'rejected';
  }
}
